import {
  Table as ChakraTable,
  Thead,
  Td,
  Th,
  Tr,
  Tbody,
  Box,
  Flex,
  Skeleton,
  Tfoot,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  Row,
  HeaderGroup,
  Table,
} from '@tanstack/react-table';
import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { FacilitiesTableMenu } from './FacilitiesTableMenu';
import {
  FACILITIES_TABLE_CONFIG,
  FacilityTableColumnKey,
  FacilitiesTableRowData,
  FacilitiesTableTotalRowData,
  FacilityTableRowConfig,
  getAttributeValueRenderMethod,
} from './facilitiesTableOptions';
import { AttributeValueFragmentFragmentGQLType } from 'packages/attribute/attribute.gql.generated';
import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { ColumnDisplaySettingsButton } from 'packages/common-frontend/components/TanStack/ColumnReordering';
import {
  createDefaultTableColumnConfiguration,
  useTableColumnPreferences,
} from 'packages/common-frontend/hooks/useTableColumnPreferences';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider/useCreditFacilities';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { SortIcon } from '~/packages/common-frontend/tables/SortIcon';
import { useSimpleColorMode } from '~/packages/common-frontend/theme/utils';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';

// Constants
const useFixedFacilitiesTableColumnStyles = () => {
  const mode = useSimpleColorMode();
  return {
    left: 0,
    zIndex: 2,
    boxShadow: '0 0 4px hsl(0deg 0% 76% / 35%)',
    borderRight: `1px solid ${mode(colors.gray[100], colors.gray[800])}`,
  } as const;
};

const TABLE_KEY = 'core.creditFacilities.facilitiesTable';

const DEFAULT_TABLE_CONFIG = (
  isSlsAgentLender: boolean,
  facilitiesTableColumnConfig: Record<string, FacilityTableRowConfig>,
) => {
  return createDefaultTableColumnConfiguration({
    tableKey: TABLE_KEY,
    columns: Object.entries(facilitiesTableColumnConfig)
      .filter(
        ([columnName]) =>
          !(columnName === 'externalFacilityId' && !isSlsAgentLender),
      )
      .map(([columnName, columnConfig]) => ({
        columnKey: columnName,
        isVisible: true,
        isFixed: !!columnConfig.fixed,
      })),
  });
};

// Fragments
type FacilitiesTableCellProps = {
  children: React.ReactNode;
  enableFacilitySelection?: boolean;
  fixed?: boolean;
  onClick: () => void;
};
const FacilitiesTableCell = ({
  children,
  enableFacilitySelection = false,
  fixed = false,
  onClick,
}: FacilitiesTableCellProps) => {
  const mode = useSimpleColorMode();
  const fixedStyles = useFixedFacilitiesTableColumnStyles();
  const fixedProps = {
    _hover: enableFacilitySelection
      ? {
          backgroundColor: mode(colors.gray[50], colors.gray[900]),
          textDecoration: 'underline',
        }
      : {},
    cursor: enableFacilitySelection ? 'pointer' : 'default',
    onClick: enableFacilitySelection ? onClick : () => {},
    position: 'sticky',
    style: fixedStyles,
  } as const;
  return (
    <Td
      backgroundColor={mode('white', 'gray.950')}
      maxWidth="14rem"
      minWidth="14rem"
      padding="1rem"
      verticalAlign="top"
      width="14rem"
      {...(fixed ? fixedProps : {})}
    >
      <Typography
        inline
        variant="base-muted"
        colorScheme={fixed && enableFacilitySelection ? 'blue' : 'gray'}
      >
        <Flex
          as="span"
          alignItems="center"
          gap="0.5rem"
          overflow="hidden"
          wordBreak="break-word"
        >
          {children}
        </Flex>
      </Typography>
    </Td>
  );
};

type FacilitiesTableRowProps = {
  enableFacilitySelection?: boolean;
  row: Row<FacilitiesTableRowData>;
  onClick: () => void;
  dynamicColumns: Record<string, FacilityTableRowConfig>;
};
const FacilitiesTableRowComponent = ({
  enableFacilitySelection = false,
  row,
  onClick,
  dynamicColumns,
}: FacilitiesTableRowProps) => {
  return (
    <Tr>
      {row.getVisibleCells().map((cell) => {
        const columnId = cell.column.id as FacilityTableColumnKey;
        return (
          <FacilitiesTableCell
            key={cell.id}
            fixed={dynamicColumns[columnId]?.fixed}
            enableFacilitySelection={enableFacilitySelection}
            onClick={() => onClick()}
          >
            {flexRender(
              dynamicColumns[columnId]?.render(cell.getValue()),
              cell.getContext(),
            )}
          </FacilitiesTableCell>
        );
      })}
    </Tr>
  );
};

type FacilitiesTableHeaderCellProps = {
  children: React.ReactNode;
  columnCanSort: boolean;
  fixed?: boolean;
  onClick?: (event: unknown) => void;
};
const FacilitiesTableHeaderCell = ({
  children,
  columnCanSort,
  fixed = false,
  onClick = () => {},
}: FacilitiesTableHeaderCellProps) => {
  const mode = useSimpleColorMode();
  const fixedStyles = useFixedFacilitiesTableColumnStyles();
  return (
    <Th
      background={mode(colors.gray[50], colors.gray[900])}
      cursor={columnCanSort ? 'pointer' : 'default'}
      maxWidth="14rem"
      minWidth="14rem"
      padding="1rem"
      position="sticky"
      top={0}
      verticalAlign="top"
      width="14rem"
      style={fixed ? { ...fixedStyles, top: 0, zIndex: 3 } : { zIndex: 2 }}
      onClick={onClick}
    >
      <Typography inline variant="base" fontWeight="medium">
        <Flex alignItems="baseline" justifyContent="space-between" gap="0.5rem">
          {children}
        </Flex>
      </Typography>
    </Th>
  );
};

type FacilitiesTableHeaderRowProps = {
  headers: HeaderGroup<
    FacilitiesTableRowData & { [key: string]: string | number | boolean | null }
  >['headers'];
  organizationName: string;
  dynamicColumns: Record<string, FacilityTableRowConfig>;
};

const FacilitiesTableHeaderRow = ({
  headers,
  organizationName,
  dynamicColumns,
}: FacilitiesTableHeaderRowProps) => {
  return (
    <Tr>
      {headers.map((header) => {
        const columnCanSort = header.column.getCanSort();
        const handleSortClick = header.column.getToggleSortingHandler();
        const columnId = header.column.id as FacilityTableColumnKey;
        return (
          <FacilitiesTableHeaderCell
            key={header.id}
            fixed={dynamicColumns[columnId]?.fixed}
            onClick={handleSortClick}
            columnCanSort={columnCanSort}
          >
            {flexRender(
              header.column.columnDef.header
                ?.toString()
                .replace('{organizationName}', organizationName),
              header.getContext(),
            )}
            <SortIcon
              columnCanSort={columnCanSort}
              sorted={header.column.getIsSorted()}
            />
          </FacilitiesTableHeaderCell>
        );
      })}
    </Tr>
  );
};

type FacilitiesTableFooterCellProps = {
  children: React.ReactNode;
  fixed?: boolean;
};
const FacilitiesTableFooterCell = ({
  children,
  fixed = false,
}: FacilitiesTableFooterCellProps) => {
  const mode = useSimpleColorMode();
  const fixedStyles = useFixedFacilitiesTableColumnStyles();
  return (
    <Th
      background={mode(colors.aurora[50], colors.aurora[900])}
      maxWidth="14rem"
      minWidth="14rem"
      padding="1rem"
      position="sticky"
      bottom={0}
      verticalAlign="top"
      width="14rem"
      style={fixed ? { ...fixedStyles, bottom: 0, zIndex: 3 } : { zIndex: 2 }}
    >
      <Typography inline variant="base" fontWeight="medium">
        <Flex alignItems="center" gap="0.5rem">
          {children}
        </Flex>
      </Typography>
    </Th>
  );
};
type FacilitiesTableFooterRowProps = {
  headers: HeaderGroup<FacilitiesTableRowData>['headers'];
  totalsRow: FacilitiesTableTotalRowData;
  dynamicColumns: Record<string, FacilityTableRowConfig>;
};
const FacilitiesTableFooterRow = ({
  headers,
  totalsRow,
  dynamicColumns,
}: FacilitiesTableFooterRowProps) => {
  return (
    <Tr>
      {headers.map((header) => {
        const columnId = header.column.id as FacilityTableColumnKey;
        return (
          <FacilitiesTableFooterCell
            key={header.id}
            fixed={dynamicColumns[columnId]?.fixed}
          >
            {flexRender(
              dynamicColumns[columnId]?.render(totalsRow[columnId]),
              header.getContext(),
            )}
          </FacilitiesTableFooterCell>
        );
      })}
    </Tr>
  );
};

// Dual Scrollbar Table Component
type FacilitiesTableWithDualScrollbarProps = {
  mode: (light: string, dark: string) => string;
  table: Table<FacilitiesTableRowData>;
  organizationName: string;
  dynamicColumns: Record<string, FacilityTableRowConfig>;
  enableFacilitySelection: boolean;
  creditFacilities: CreditFacility[];
  setCreditFacilityId: (id: string) => void;
  navigate: ReturnType<typeof useNavigate>;
  totalsRow: FacilitiesTableTotalRowData;
};

const FacilitiesTableWithDualScrollbar = ({
  mode,
  table,
  organizationName,
  dynamicColumns,
  enableFacilitySelection,
  creditFacilities,
  setCreditFacilityId,
  navigate,
  totalsRow,
}: FacilitiesTableWithDualScrollbarProps) => {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState<number>(0);

  const rowCount = table.getRowModel().rows.length;
  const columnCount = table.getVisibleLeafColumns().length;

  // Update table width after render and on resize
  useEffect(() => {
    const updateWidth = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.scrollWidth);
      }
    };

    // Initial measurement after render
    updateWidth();

    // Re-measure on window resize
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [rowCount, columnCount]);

  const syncScroll = useCallback((source: 'top' | 'table') => {
    if (!topScrollRef.current || !tableContainerRef.current) return;
    if (source === 'top') {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    } else {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  }, []);

  return (
    <Box
      borderWidth="thin"
      borderColor={mode(colors.gray[100], colors.gray[800])}
      position="relative"
    >
      {/* Top scrollbar */}
      <Box
        ref={topScrollRef}
        overflowX="scroll"
        overflowY="hidden"
        onScroll={() => syncScroll('top')}
        css={{
          '&::-webkit-scrollbar': {
            height: '12px',
          },
          '&::-webkit-scrollbar-track': {
            background: mode(colors.gray[100], colors.gray[800]),
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode(colors.gray[300], colors.gray[600]),
            borderRadius: '6px',
          },
        }}
      >
        <Box style={{ width: tableWidth || '100%', height: '1px' }} />
      </Box>
      {/* Table container */}
      <Box
        ref={tableContainerRef}
        overflowX="scroll"
        overflowY="auto"
        maxHeight="70vh"
        onScroll={() => syncScroll('table')}
        css={{
          '&::-webkit-scrollbar': {
            height: '12px',
            width: '12px',
          },
          '&::-webkit-scrollbar-track': {
            background: mode(colors.gray[100], colors.gray[800]),
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode(colors.gray[300], colors.gray[600]),
            borderRadius: '6px',
          },
        }}
      >
        <ChakraTable
          ref={tableRef}
          style={{ borderCollapse: 'separate', borderSpacing: 0 }}
        >
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <FacilitiesTableHeaderRow
                key={headerGroup.id}
                headers={headerGroup.headers}
                organizationName={organizationName}
                dynamicColumns={dynamicColumns}
              />
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <FacilitiesTableRowComponent
                key={row.id}
                row={row}
                dynamicColumns={dynamicColumns}
                enableFacilitySelection={enableFacilitySelection}
                onClick={() => {
                  const creditFacility = creditFacilities.find(
                    ({ id }) => id === row.id,
                  );
                  if (creditFacility?.id) {
                    setCreditFacilityId(creditFacility.id);
                    navigate(
                      `/portfolio/summary?creditFacility=${creditFacility.id}`,
                    );
                  }
                }}
              />
            ))}
          </Tbody>
          <Tfoot>
            {table.getFooterGroups().map((footerGroup) => (
              <FacilitiesTableFooterRow
                key={footerGroup.id}
                headers={footerGroup.headers}
                totalsRow={totalsRow}
                dynamicColumns={dynamicColumns}
              />
            ))}
          </Tfoot>
        </ChakraTable>
      </Box>
    </Box>
  );
};

// Exports
type FacilitiesTableProps = {
  creditFacilities: CreditFacility[];
  enableFacilitySelection: boolean;
  rows: FacilitiesTableRowData[];
  totalsRow: FacilitiesTableTotalRowData;
  hasBorrowingBase: boolean;
  attributeValues: AttributeValueFragmentFragmentGQLType[];
  isLoading: boolean;
};
export const FacilitiesTable = ({
  creditFacilities,
  enableFacilitySelection,
  rows,
  totalsRow,
  hasBorrowingBase,
  attributeValues,
  isLoading,
}: FacilitiesTableProps) => {
  const mode = useSimpleColorMode();
  const { user } = useUserAuthOrThrow();
  const navigate = useNavigate();
  const { setCreditFacilityId } = useCreditFacilityContext();
  const { isSlsAgentLender } = useFeatureFlags();

  const dynamicColumns = useMemo(() => {
    const attributeValuesConfig = attributeValues.reduce<
      Record<string, FacilityTableRowConfig>
    >((acc, val) => {
      return {
        ...acc,
        [val.name]: {
          header: val.displayName,
          userTypes: ['BORROWER', 'CAPITAL_PROVIDER'],
          render: getAttributeValueRenderMethod(val.type),
          relatedToBorrowingBase: false,
        },
      };
    }, {});

    return {
      ...FACILITIES_TABLE_CONFIG(user, isSlsAgentLender),
      ...attributeValuesConfig,
    };
  }, [attributeValues, user, isSlsAgentLender]);

  // Filter out columns by user type (and feature flags)
  const allowedColumns = useMemo(
    () =>
      Object.entries(dynamicColumns)
        .filter(([_key, value]) => {
          // if there's no borrowing base, make sure that the column is not related to borrowing base
          if (!hasBorrowingBase) {
            return (
              value.userTypes.includes(user.userType) &&
              !value.relatedToBorrowingBase
            );
          }
          return value.userTypes.includes(user.userType);
        })
        .map(([accessorKey, { header }]) => ({
          accessorKey,
          header,
        })),
    [hasBorrowingBase, user, dynamicColumns],
  );

  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG(
      isSlsAgentLender,
      dynamicColumns,
    ),
    skipSettingConfig: isLoading,
  });

  // Here we need to remove any columns from the column selector that should be
  // hidden from the user because of their role (user.userType)
  const allowedColumnConfiguration = columnConfiguration
    .filter((columnConfig) =>
      allowedColumns.some(
        (allowedColumn) => allowedColumn.accessorKey === columnConfig.columnKey,
      ),
    )
    .concat([]);

  const columnNames = useMemo(
    () =>
      Object.entries(dynamicColumns).reduce<{
        [key: string]: string;
      }>((acc, [columnName, columnConfig]) => {
        const label =
          typeof columnConfig.header === 'string'
            ? columnConfig.header
            : commonUtils.toSentenceCase(columnName);

        // eslint-disable-next-line no-param-reassign
        acc[columnName] = label.replace(
          '{organizationName}',
          user.organization.name,
        );
        return acc;
      }, {}),
    [dynamicColumns, user],
  );

  const [searchQuery, setSearchQuery] = useState('');
  const filteredRows = useMemo(() => {
    if (!searchQuery) {
      return rows;
    }

    const search = searchQuery.toLowerCase();
    const isMatch = (input?: string | null): boolean => {
      if (!input) return false;
      return input.toLowerCase().includes(search);
    };

    return rows.filter((row) => {
      if (isMatch(row.creditFacilityName) || isMatch(row.externalFacilityId))
        return true;
      return false;
    });
  }, [rows, searchQuery]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data: filteredRows,
    columns: allowedColumns,
    state: { columnVisibility, sorting, columnOrder },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
    getRowId: (row) => row.creditFacilityId,
  });

  return (
    <Flex flexDirection="column" gap="1.5rem">
      <Flex alignItems="center" flexWrap="wrap" gap="0.75rem">
        <Typography variant="section-title" trim TextProps={{ flexGrow: 1 }}>
          Facilities
        </Typography>
        <Flex flexShrink={0} gap="0.5rem">
          <ColumnDisplaySettingsButton
            initialColumns={allowedColumnConfiguration}
            updateColumnConfigs={updateUserColumnConfiguration}
            columnNames={columnNames}
          />
          <FacilitiesTableMenu table={table} />
        </Flex>
        <SearchBarInput query={searchQuery} onChangeQuery={setSearchQuery} />
      </Flex>

      {isLoading ? (
        <Skeleton height="28rem" />
      ) : (
        <FacilitiesTableWithDualScrollbar
          mode={mode}
          table={table}
          organizationName={user.organization.name}
          dynamicColumns={dynamicColumns}
          enableFacilitySelection={enableFacilitySelection}
          creditFacilities={creditFacilities}
          setCreditFacilityId={setCreditFacilityId}
          navigate={navigate}
          totalsRow={totalsRow}
        />
      )}
    </Flex>
  );
};
